import { forwardRef, Injectable } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { Router } from '@angular/router'
import { StoreService } from '@se-po/se-household-setup-data-access-store'
import { ScrollHelperService } from '@se-po/shared-data-access-helpers'
import { AnalyticsService, UserDuplicateLookupService } from '@se-po/shared-data-access-services'
import { CookieService } from 'ngx-cookie-service'
import { first } from 'rxjs/operators'
import { SeFeFormService } from 'se-fe-form-service'
import { UserServiceResources as us } from 'se-resource-types'

import { SelfInfoFormService } from './self-info-form.service'

@Injectable({
  providedIn: 'root',
  useExisting: forwardRef(() => SelfInfoSubmitService),
  deps: [Router]
})
export abstract class SelfInfoSubmitter {
  public abstract agelock: boolean
  public abstract matchingDupe: us.UserDuplicateLookupResult
  public abstract matchingEmail: string
  public abstract submitting: boolean
  public abstract underAgeCookieName: string
  public abstract submit(store: StoreService): void
  public abstract emailMatchSignIn(emailMatchSignIn: HTMLFormElement): void
  public abstract validateAndSubmit(
    form: UntypedFormGroup,
    store: StoreService,
    childForms?: { personaInfo: UntypedFormGroup; contactInfo: UntypedFormGroup }
  ): void
}

@Injectable({
  providedIn: 'root'
})
export class SelfInfoSubmitService implements SelfInfoSubmitter {
  public agelock = false
  public matchingEmail: string
  public matchingDupe: us.UserDuplicateLookupResult
  public submitting = false
  public underAgeCookieName = 'agelock'

  constructor(
    private analyticsService: AnalyticsService,
    private cookieService: CookieService,
    private dupeService: UserDuplicateLookupService,
    private formService: SeFeFormService,
    public router: Router,
    private scrollHelper: ScrollHelperService,
    private selfFormService: SelfInfoFormService,
  ) {}

  public validateAndSubmit(
    form: UntypedFormGroup,
    store: StoreService,
    childForms?: { personaInfo: UntypedFormGroup; contactInfo: UntypedFormGroup }
  ): void {
    if (this.agelock) return

    this.submitting = true
    if (!this.formService.isValid(form)) {
      this.submitting = false
      this.analyticsService.trackFormErrors(form)
      if (childForms?.personaInfo?.controls?.dateOfBirth?.errors?.required) {
        return
      }
      else if (childForms?.personaInfo?.controls?.dateOfBirth?.errors?.dob18) {
        this.cookieService.set(this.underAgeCookieName, '1', {
          expires: 1, // 1 day
        })
        this.agelock = true
      }
      return
    }
    this.submit(store)
  }

  public submit(store: StoreService): void {
    this.analyticsService.seEvent('ContinueToSignUpClick')
    store.accountInfo = this.selfFormService.form.value
    this.dupeService.search(store.userDuplicateLookupParams)
      .pipe(first())
      .subscribe((result) => {
        store.dupes = result
        if (!result) return this.navigate([store.basePath, 'create-password'])

        else if (result.user_duplicate_lookup_results[0].confidence === 'exact_email') {
          this.matchingEmail = result.email
          this.matchingDupe = result.user_duplicate_lookup_results[0]
          this.submitting = false
          this.scrollHelper.scrollTo(0, 0)
          this.analyticsService.trackError('EmailAddressAlreadyInUse')
        }
        else {
          store.nextPath = 'create-password'
          return this.navigate([store.basePath, 'match'])
        }
      })
  }

  public emailMatchSignIn(emailMatchSignIn: HTMLFormElement): void {
    this.analyticsService.seEvent('BackToSignInClick')
    emailMatchSignIn.nativeElement.submit()
  }

  private navigate(path: string[]): void {
    this.submitting = false
    this.router.navigate(path)
  }
}
